import {
	dispatchAcknowledgedEvent,
	openResourceSection,
} from '../../core/resourceEvents';
import {
	openFirmwareManagementSection as openFirmwareTab,
} from '../maintenanceTabs/adapter';
import {
	FIRMWARE_AP_IMAGES_SECTION_EVENT,
	FIRMWARE_AP_TASKS_SECTION_EVENT,
	FIRMWARE_LIST_EVENTS,
	FIRMWARE_SWITCH_IMAGES_SECTION_EVENT,
} from './events';
import {searchTextForQuery} from './resolver';

const AP_IMAGES = new Set(['maintenance.ap_firmwares']);
const AP_TASKS = new Set(['maintenance.ap_firmware_tasks']);
const SWITCH_IMAGES = new Set(['maintenance.switch_firmwares']);

/**
 * Open one firmware accordion.
 *
 * Both lists are inside the Firmware Management tab, which only mounts its
 * accordions once it is the tab showing - so the tab is settled first and
 * the accordion asked for second. Doing it the other way round dispatches
 * at a component that is not there.
 *
 * This used to also press the accordion's Add control. The three Add
 * capabilities now open their form directly (see `modal.js`): pressing a
 * control that only exists while its accordion is expanded meant a request
 * could land on the right list and still report the control unavailable.
 *
 * @param {string} eventName accordion event
 * @param {string} sectionId agent section id
 * @param {Set<string>} allowed sections this opener serves
 * @return {Promise<object>} what the page reported opening
 */
async function openFirmwareSection(eventName, sectionId, allowed) {
	await openFirmwareTab();
	return openResourceSection(eventName, sectionId, allowed);
}

/**
 * Open the Access Point Firmwares accordion.
 * @return {Promise<object>} completion acknowledgement
 */
export function openApFirmwaresSection() {
	return openFirmwareSection(
		FIRMWARE_AP_IMAGES_SECTION_EVENT, 'maintenance.ap_firmwares',
		AP_IMAGES,
	);
}

/**
 * Open the Access Point Firmware Scheduled Tasks accordion.
 * @return {Promise<object>} completion acknowledgement
 */
export function openApFirmwareTasksSection() {
	return openFirmwareSection(
		FIRMWARE_AP_TASKS_SECTION_EVENT, 'maintenance.ap_firmware_tasks',
		AP_TASKS,
	);
}

/**
 * Open the Switch Firmwares accordion.
 * @return {Promise<object>} completion acknowledgement
 */
export function openSwitchFirmwaresSection() {
	return openFirmwareSection(
		FIRMWARE_SWITCH_IMAGES_SECTION_EVENT, 'maintenance.switch_firmwares',
		SWITCH_IMAGES,
	);
}

/**
 * Build the filter handler for one list.
 *
 * What lands in the search box is the text the list can search by, not the
 * name the agent displayed - see `searchTextForQuery`.
 *
 * @param {string} kind entity kind, which is also the command's `resource`
 * @return {Function} filter handler
 */
function filterFor(kind) {
	return (command) => {
		const query = String(command.params?.query || '').trim();
		if (!query) return Promise.reject(new Error('invalid_firmware_filter'));
		return dispatchAcknowledgedEvent(FIRMWARE_LIST_EVENTS[kind].filter, {
			query: searchTextForQuery(kind, query),
			resource: kind,
		});
	};
}

/**
 * Build the focus handler for one list.
 *
 * `action_target` travels so a read action pressed from chat - downloading a
 * task - can be carried out by the list that owns the row, which is the only
 * place that knows how.
 *
 * @param {string} kind entity kind
 * @return {Function} focus handler
 */
function focusFor(kind) {
	return (command) => {
		const entityId = command.params?.entity_id;
		if (typeof entityId !== 'string' || !entityId) {
			return Promise.reject(new Error('invalid_firmware_entity'));
		}
		return dispatchAcknowledgedEvent(FIRMWARE_LIST_EVENTS[kind].focus, {
			entityId,
			action_target: command.params?.action_target,
			action_activation: command.params?.action_activation,
			resource: kind,
		});
	};
}

export const applyApFirmwareFilter = filterFor('ap_firmware');
export const applyApFirmwareTaskFilter = filterFor('ap_firmware_task');
export const applySwitchFirmwareFilter = filterFor('switch_firmware');
export const focusApFirmware = focusFor('ap_firmware');
export const focusApFirmwareTask = focusFor('ap_firmware_task');
export const focusSwitchFirmware = focusFor('switch_firmware');
